import React, { useMemo, useRef } from "react";

import AutoFixHighIcon from "@mui/icons-material/AutoFixHigh";
import CancelIcon from "@mui/icons-material/Cancel";
import { Chip, IconButton, Stack, Typography } from "@mui/material";
import { Notice } from "components/Prompts/Notice";
import { MaterialTooltip } from "components/tooltips/MaterialTooltip";
import { useIsViewerPermission, usePrompt } from "hooks";

import { useFilesPickerContext } from "../hooks/files-picker-context";

export const FileUploader: React.FC = () => {
  const {
    attachments,
    oldFiles,
    removeFile,
    removeOldFile,
    loading,
    addFile,
    isSelectionDisabled,
    singleFileSelection,
    acceptedFilesExtensions,
    onSelectFileForMe,
    isFileForMeSelected
  } = useFilesPickerContext();
  const inputRef = useRef<HTMLInputElement>(null);
  const isAnalystViewerPermission = useIsViewerPermission("file_upload");
  const openPrompt = usePrompt();

  const onAttachClick = (e: React.MouseEvent) => {
    if (loading || isSelectionDisabled()) return;
    if (isAnalystViewerPermission) {
      openPrompt({ Component: Notice, props: { type: "trial" } });
      return;
    }
    e.stopPropagation();
    inputRef.current?.click();
  };

  // Generate accept attribute based on acceptedFilesExtensions
  const acceptAttribute = useMemo(() => {
    if (acceptedFilesExtensions && acceptedFilesExtensions.length) {
      return acceptedFilesExtensions.join(",");
    }
    // Default: allow images and PDFs
    return "image/*,.pdf,application/pdf";
  }, [acceptedFilesExtensions]);

  return (
    <Stack>
      <Stack direction="row" flexWrap="wrap" columnGap="12px" sx={{ overflowY: "auto", maxHeight: "150px" }}>
        {oldFiles.map((item) => (
          <Stack direction="row" key={item.id}>
            <Typography sx={{ fontSize: "15px", color: "#496682" }}>{item.name}</Typography>
            <IconButton
              onClick={() => {
                if (loading) return;
                removeOldFile(item);
              }}
              size="sm"
            >
              <CancelIcon />
            </IconButton>
          </Stack>
        ))}
        {attachments.map((item) => (
          <Stack direction="row" key={item.lastModified}>
            <MaterialTooltip placement="top" title={item.name}>
              <Typography
                sx={{
                  fontSize: "15px",
                  color: "#496682",
                  whiteSpace: "nowrap",
                  overflow: "hidden",
                  textOverflow: "ellipsis",
                  maxWidth: "250px"
                }}
              >
                {item.name}
              </Typography>
            </MaterialTooltip>
            <IconButton onClick={() => !loading && removeFile(item)} size="sm">
              <CancelIcon />
            </IconButton>
          </Stack>
        ))}
      </Stack>
      <Stack direction="row" gap="12px" justifyContent="space-between">
        <Typography
          sx={{
            color: isSelectionDisabled() ? "#A0AEC0" : "#26A6AE",
            cursor: isSelectionDisabled() ? "default" : "pointer",
            pointerEvents: isSelectionDisabled() ? "none" : "auto",
            width: "fit-content"
          }}
          onClick={onAttachClick}
        >
          + Upload File
        </Typography>
        {onSelectFileForMe && (
          <Chip
            icon={<AutoFixHighIcon sx={{ fontSize: "16px" }} />}
            label="Select a file for me"
            onClick={onSelectFileForMe}
            disabled={!!attachments.length}
            variant={isFileForMeSelected ? "filled" : "outlined"}
            sx={{
              height: "28px",
              borderRadius: "14px",
              cursor: attachments.length ? "default" : "pointer",
              ...(isFileForMeSelected
                ? {
                    backgroundColor: "#071F35",
                    color: "#fff",
                    "& .MuiChip-icon": { color: "#fff" },
                    "&:hover": { backgroundColor: "#1a3a52" }
                  }
                : {
                    borderColor: "#7B92A9",
                    color: "#496682",
                    "& .MuiChip-icon": { color: "#496682" },
                    "&:hover": { backgroundColor: "#f5f6f7" }
                  })
            }}
          />
        )}
      </Stack>
      <input
        disabled={isSelectionDisabled()}
        onChange={addFile}
        ref={inputRef}
        accept={acceptAttribute}
        hidden
        type="file"
        multiple={!singleFileSelection}
      />
    </Stack>
  );
};
